import { DatePicker } from '@mantine/dates';
import { FormControl, TextField } from '@mui/material';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

const isUSLocale =
  (navigator.language ||
    navigator.userLanguage ||
    navigator.browserLanguage) === 'en-US';

const datesFormatList = isUSLocale
  ? [
      'MM-DD-YY',
      'MM/DD/YY',
      'MM.DD.YY',
      'MM-DD-YYYY',
      'MM/DD/YYYY',
      'MM.DD.YYYY',
      'DD-MM-YY',
      'DD/MM/YY',
      'DD.MM.YY',
      'DD-MM-YYYY',
      'DD/MM/YYYY',
      'DD.MM.YYYY',
      'YYYY-MM-DD',
      'YYYY/MM/DD',
      'YYYY.MM.DD',
    ]
  : [
      'DD-MM-YY',
      'DD/MM/YY',
      'DD.MM.YY',
      'DD-MM-YYYY',
      'DD/MM/YYYY',
      'DD.MM.YYYY',
      'MM-DD-YY',
      'MM/DD/YY',
      'MM.DD.YY',
      'MM-DD-YYYY',
      'MM/DD/YYYY',
      'MM.DD.YYYY',
      'YYYY-MM-DD',
      'YYYY/MM/DD',
      'YYYY.MM.DD',
    ];

const getShortcutDates = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);

  const startOfCurrentWeek = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() - today.getDay() + 1,
    0,
    0,
    0,
  );
  startOfCurrentWeek.setHours(0, 0, 0, 0);
  const endOfCurrentWeek = new Date(
    startOfCurrentWeek.getTime() + 7 * 24 * 60 * 60 * 1000 - 1,
  );
  endOfCurrentWeek.setHours(0, 0, 0, 0);

  return {
    today,
    tomorrow,
    startOfCurrentWeek,
    endOfCurrentWeek,
  };
};

const convertLabeledDateRangeToDates = (
  dueDate,
  rangeSelectionDone,
  format,
) => {
  if (!dueDate) return {};

  const { today, tomorrow, startOfCurrentWeek, endOfCurrentWeek } =
    getShortcutDates();

  switch (dueDate) {
    case 'today': {
      const localizedDate = format
        ? moment(today).format(format)
        : today.toLocaleDateString('en-CA');
      return {
        rangeStart: localizedDate,
        rangeEnd: rangeSelectionDone ? localizedDate : null,
      };
    }
    case 'tomorrow': {
      const localizedDate = format
        ? moment(tomorrow).format(format)
        : tomorrow.toLocaleDateString('en-CA');
      return {
        rangeStart: localizedDate,
        rangeEnd: rangeSelectionDone ? localizedDate : null,
      };
    }
    case 'this-week': {
      return {
        rangeStart: format
          ? moment(startOfCurrentWeek).format(format)
          : startOfCurrentWeek.toLocaleDateString('en-CA'),
        rangeEnd: format
          ? moment(endOfCurrentWeek).format(format)
          : endOfCurrentWeek.toLocaleDateString('en-CA'),
      };
    }
    case 'overdue': {
      return {
        rangeStart: '',
        rangeEnd: '',
      };
    }
    case 'no-due-date': {
      return {
        rangeStart: '',
        rangeEnd: '',
      };
    }
    default: {
      return {};
    }
  }
};

const getStringifiedDateValue = (
  { dueStartDate, dueEndDate, dueDate },
  dateRangeSelectionDone,
  format,
) => {
  const { rangeStart, rangeEnd } = convertLabeledDateRangeToDates(
    dueDate?.[0]?._id,
    dateRangeSelectionDone,
    format,
  );

  let startDate = null;
  let endDate = null;

  if (dueStartDate?.length || rangeStart) {
    startDate =
      rangeStart ||
      (format
        ? moment(dueStartDate?.[0]._id).format(format)
        : new Date(dueStartDate?.[0]._id).toLocaleDateString('en-CA'));
  }

  if (dueEndDate?.length || rangeEnd || rangeStart) {
    endDate = rangeStart
      ? rangeEnd
      : dueEndDate?.length
        ? format
          ? moment(dueEndDate?.[0]._id).format(format)
          : new Date(dueEndDate?.[0]._id).toLocaleDateString('en-CA')
        : null;
  }

  return [startDate, endDate];
};

const convertDateRangeValueToShortcutLabel = (value) => {
  if (!Array.isArray(value)) {
    return value;
  }

  const { today, tomorrow, startOfCurrentWeek, endOfCurrentWeek } =
    getShortcutDates();

  const dueStartDate = new Date(value[0]);
  dueStartDate.setHours(0, 0, 0, 0);

  const isSecondDateNeeded = value[1] && value[0] !== value[1];

  let dueEndDate = null;

  if (isSecondDateNeeded) {
    dueEndDate = new Date(value[1]);
    dueEndDate.setHours(0, 0, 0, 0);
  }

  switch (true) {
    case !isSecondDateNeeded && dueStartDate?.getTime() === today.getTime(): {
      return { _id: 'today' };
    }
    case !isSecondDateNeeded &&
      dueStartDate?.getTime() === tomorrow.getTime(): {
      return { _id: 'tomorrow' };
    }
    case isSecondDateNeeded &&
      dueStartDate?.getTime() === startOfCurrentWeek.getTime() &&
      dueEndDate?.getTime() === endOfCurrentWeek.getTime(): {
      return { _id: 'this-week' };
    }
    case !value[1] && !value[0]: {
      return { _id: 'no-due-date' };
    }
    default: {
      return value;
    }
  }
};

const DateRangeSelection = ({
  dueDate,
  dueDateOptions,
  dueEndDate,
  dueStartDate,
  isOptionChecked,
  pureDatePicker,
  onValueSelection,
  menuStyle,
}) => {
  const [startDateInputValue, setStartDateInputValue] = useState('');
  const [endDateInputValue, setEndDateInputValue] = useState('');
  const [shownDate, setShownDate] = useState(new Date());
  const [dateRangeSelectionDone, setDateRangeSelectionDone] = useState(false);

  const [startDatePickerValue, setStartDatePickerValue] = useState('');
  const [endDatePickerValue, setEndDatePickerValue] = useState('');

  const datePickerValue = useMemo(() => {
    let startValueToSet,
      endValueToSet = null;

    const [startDate, endDate] = getStringifiedDateValue(
      { dueStartDate, dueEndDate, dueDate },
      dateRangeSelectionDone,
    );

    startValueToSet = startDatePickerValue ?? startDate;
    endValueToSet = endDatePickerValue ?? endDate;

    return [startValueToSet, endValueToSet];
  }, [
    dateRangeSelectionDone,
    dueDate,
    dueEndDate,
    dueStartDate,
    endDatePickerValue,
    startDatePickerValue,
  ]);

  const handleSelection = useCallback((value) => {
    setDateRangeSelectionDone(true);
    const convertedValue = pureDatePicker
      ? value
      : convertDateRangeValueToShortcutLabel(value);
    if (Array.isArray(value)) {
      setDateRangeSelectionDone(value[0] === value[1]);
      handleStartDateInputSubmit(null, value[0] || '', true);
      handleEndDateInputSubmit(null, value[1] || '', true);
    } else {
      const [startRange, endRange] = getStringifiedDateValue(
        { dueDate: [value] },
        true,
      );
      handleStartDateInputSubmit(null, startRange || '', true);
      handleEndDateInputSubmit(null, endRange || '', true);
    }

    onValueSelection(convertedValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStartDateChange = useCallback((e) => {
    setStartDateInputValue(e.target.value);
  }, []);
  const handleEndDateChange = useCallback((e) => {
    setEndDateInputValue(e.target.value);
  }, []);

  const handleStartDateInputSubmit = useCallback(
    (e, value, skipSending) => {
      const valueToOperate = value ?? startDateInputValue;
      const startDateValueToSetMoment = moment(valueToOperate, datesFormatList);
      let startDateValueToSet = startDateValueToSetMoment.format('YYYY-MM-DD');
      startDateValueToSet =
        startDateValueToSet === 'Invalid date' ? '' : startDateValueToSet;

      let isReverseDatesNeeded = false;

      if (startDateValueToSet) {
        isReverseDatesNeeded =
          endDateInputValue &&
          startDateValueToSetMoment.isAfter(moment(endDateInputValue));
        if (isReverseDatesNeeded) {
          setStartDatePickerValue(
            new Date(endDateInputValue).toLocaleDateString('en-CA'),
          );
          setStartDateInputValue(endDateInputValue);
          setEndDatePickerValue(
            new Date(startDateValueToSet).toLocaleDateString('en-CA'),
          );
          setEndDateInputValue(startDateValueToSet);
        } else {
          setStartDatePickerValue(
            new Date(startDateValueToSet).toLocaleDateString('en-CA'),
          );
          setStartDateInputValue(startDateValueToSet);
        }
      } else {
        setStartDatePickerValue('');
        setStartDateInputValue('');
      }

      const resultRange = isReverseDatesNeeded
        ? [endDateInputValue, startDateValueToSet]
        : [startDateValueToSet, endDateInputValue];

      const convertedValue = pureDatePicker
        ? resultRange
        : convertDateRangeValueToShortcutLabel(resultRange);

      if (!skipSending) {
        onValueSelection(convertedValue);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pureDatePicker, startDateInputValue, endDateInputValue],
  );

  const handleStartDateInputEnter = useCallback((e) => {
    if (e.key !== 'Enter') {
      return;
    }
    e?.target?.blur();
  }, []);

  const handleEndDateInputSubmit = useCallback(
    (e, value, skipSending) => {
      const valueToOperate = value ?? endDateInputValue;
      const endDateValueToSetMoment = moment(valueToOperate, datesFormatList);
      let endDateValueToSet = endDateValueToSetMoment.format('YYYY-MM-DD');
      endDateValueToSet =
        endDateValueToSet === 'Invalid date' ? '' : endDateValueToSet;

      let isReverseDatesNeeded = false;

      if (endDateValueToSet) {
        isReverseDatesNeeded =
          startDateInputValue &&
          endDateValueToSetMoment.isBefore(moment(startDateInputValue));
        if (isReverseDatesNeeded) {
          setStartDatePickerValue(
            new Date(endDateValueToSet).toLocaleDateString('en-CA'),
          );
          setStartDateInputValue(endDateValueToSet);
          setEndDatePickerValue(
            new Date(startDateInputValue).toLocaleDateString('en-CA'),
          );
          setEndDateInputValue(startDateInputValue);
        } else {
          setEndDatePickerValue(
            new Date(endDateValueToSet).toLocaleDateString('en-CA'),
          );
          setEndDateInputValue(endDateValueToSet);
        }
      } else {
        setEndDatePickerValue('');
        setEndDateInputValue('');
      }

      const resultRange = isReverseDatesNeeded
        ? [endDateValueToSet, startDateInputValue]
        : [startDateInputValue, endDateValueToSet];

      const convertedValue = pureDatePicker
        ? resultRange
        : convertDateRangeValueToShortcutLabel(resultRange);

      if (!skipSending) {
        onValueSelection(convertedValue);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [endDateInputValue, pureDatePicker, startDateInputValue],
  );

  const handleEndDateInputEnter = useCallback((e) => {
    if (e.key !== 'Enter') {
      return;
    }
    e?.target?.blur();
  }, []);

  useEffect(() => {
    const [startDate, endDate] = getStringifiedDateValue(
      { dueStartDate, dueEndDate, dueDate },
      true,
    );

    const startValueToSet = startDate || '';
    const endValueToSet = endDate || '';

    if (startValueToSet) {
      handleStartDateInputSubmit(null, startValueToSet, true);
    }

    if (endValueToSet) {
      handleEndDateInputSubmit(null, endValueToSet, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (endDatePickerValue) {
      setShownDate(new Date(endDatePickerValue));
    } else if (startDatePickerValue) {
      setShownDate(new Date(startDatePickerValue));
    } else if (datePickerValue) {
      setShownDate(datePickerValue[1] || datePickerValue[0]);
    }
  }, [datePickerValue, startDatePickerValue, endDatePickerValue]);

  return (
    <div
      className="filter-menu min-w-max pt-2"
      style={{ width: '270px', overflow: 'hidden', ...menuStyle }}
    >
      <div className="filter-list">
        {pureDatePicker ? (
          <div className="p-2">
            <DatePicker
              className="filter-date-picker"
              type="range"
              allowSingleDateInRange
              value={datePickerValue}
              date={shownDate}
              onDateChange={setShownDate}
              onChange={handleSelection}
            />
          </div>
        ) : (
          <>
            <div className="dateRangeselection-dates-picker-container">
              <DatePicker
                className="filter-date-picker"
                type="range"
                allowSingleDateInRange
                value={datePickerValue}
                date={shownDate}
                onDateChange={setShownDate}
                onChange={handleSelection}
              />
            </div>
            <div className="dateRangeselection-dates-inputs-container">
              <FormControl sx={{ height: '40px', margin: 0, width: '115px' }}>
                <TextField
                  className="dateRangeselection-date-input"
                  label="Start date"
                  size="small"
                  onChange={handleStartDateChange}
                  value={startDateInputValue}
                  onBlur={handleStartDateInputSubmit}
                  onKeyDown={handleStartDateInputEnter}
                />
              </FormControl>
              ⎯
              <FormControl sx={{ height: '40px', margin: 0, width: '115px' }}>
                <TextField
                  className="dateRangeselection-date-input"
                  label="End date"
                  size="small"
                  onChange={handleEndDateChange}
                  value={endDateInputValue}
                  onBlur={handleEndDateInputSubmit}
                  onKeyDown={handleEndDateInputEnter}
                  sx={{ fontSize: '10px' }}
                />
              </FormControl>
            </div>
            {dueDateOptions.map((item, index) => (
              <label key={item?._id} className="filter-item">
                <input
                  className="filter-checkbox"
                  type="radio"
                  onChange={() => {
                    handleSelection(item);
                  }}
                  checked={isOptionChecked(item)}
                />
                {item.icon && <div className="w-[20%]">{item.icon}</div>}
                <div className="w-[90%]">{item.label}</div>
              </label>
            ))}
          </>
        )}
      </div>
      <div className="dateRangeselection-clear-btn-container">
        <button
          className="text-sm text-gray-600 cursor-pointer"
          onClick={() => handleSelection('clear')}
        >
          <FormattedMessage id="CLEAR" defaultMessage="Clear" />
        </button>
      </div>
    </div>
  );
};

export default DateRangeSelection;
